// src/components/partner/RecentTransactionsTable.tsx
//
// RecentTransactionsTable
// - Renders partner transaction history using the shared table UI (StandardTable*).
// - Provides consistent pagination UX (top: total + jump, bottom: pagination).
// - Avoids row background highlighting to prevent visual noise; only refunded rows are marked as inactive.
//
// Notes
// - This component is UI-only; it does not own data fetching.
// - The parent page controls pagination state and passes `transactions`, `page`, and `totalCount`.

import React, { useCallback, useMemo } from 'react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import {
  AddCircleOutline,
  ReceiptLong,
  RemoveCircleOutline,
  ReplayOutlined,
  ShoppingCartOutlined,
} from '@mui/icons-material';

import { timestampToDate } from '@utils/time';
import { getPartnerTransactionTypeMeta } from '@constants/partnerLabels';

import {
  StandardTableFrame,
  StandardTableToolbar,
  StandardPagination,
  cellSx,
} from '@components/common/table';

export type PartnerTransactionRow = {
  partnerTransactionId: number | string;
  type: string;

  partnerTransactionCode: string;
  orderCode: string;
  productCode: string;
  productName: string;

  quantity: number;
  previousBalance: number;
  addedAmount: number;

  balance?: number;
  createdAt: string | number;
};

type Props = {
  transactions: PartnerTransactionRow[];
  isLoading: boolean;

  page: number;
  totalCount: number;
  pageSize: number;

  onChangePage: (_: React.ChangeEvent<unknown>, value: number) => void;
  skeletonRows?: number;
};

const transactionIconSx = {
  fontSize: 16,
  flex: '0 0 auto',
} as const;

const USD_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toUsdNumberDisplay = (amount: number): string => {
  if (!Number.isFinite(amount)) return '0.00';
  return USD_NUMBER_FORMATTER.format(amount);
};

const getTransactionColor = (type: string) => {
  switch (type) {
    case 'PAID_ORDER':
      return 'error.main';
    case 'REFUNDED_ORDER':
      return 'info.main';
    case 'DEPOSIT':
      return 'success.main';
    case 'WITHDRAW':
      return 'warning.main';
    default:
      return 'text.secondary';
  }
};

const renderTransactionIcon = (type: string) => {
  switch (type) {
    case 'PAID_ORDER':
      return <ShoppingCartOutlined sx={transactionIconSx} />;
    case 'REFUNDED_ORDER':
      return <ReplayOutlined sx={transactionIconSx} />;
    case 'DEPOSIT':
      return <AddCircleOutline sx={transactionIconSx} />;
    case 'WITHDRAW':
      return <RemoveCircleOutline sx={transactionIconSx} />;
    default:
      return <ReceiptLong sx={transactionIconSx} />;
  }
};

const PartnerTransactionTypeDisplay = ({ type }: { type: string }) => {
  const meta = getPartnerTransactionTypeMeta(type);

  return (
    <Stack
      component="span"
      direction="row"
      spacing={0.75}
      alignItems="center"
      justifyContent="flex-start"
      sx={{
        color: getTransactionColor(type),
        minWidth: 0,
      }}
    >
      {renderTransactionIcon(type)}
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: 'inherit',
          fontWeight: 800,
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label}
      </Typography>
    </Stack>
  );
};

const RecentTransactionsTable = React.forwardRef<HTMLDivElement, Props>(
  (
    { transactions, isLoading, page, totalCount, pageSize, onChangePage, skeletonRows = 6 },
    ref,
  ) => {
    const rows = useMemo(() => transactions ?? [], [transactions]);

    const handleChangePage = useCallback(
      (next: number) => onChangePage({} as any, next),
      [onChangePage],
    );

    const getFinalBalance = useCallback((t: PartnerTransactionRow) => {
      const amount = Number(t.addedAmount ?? 0);
      return typeof t.balance === 'number'
        ? t.balance
        : Number(t.previousBalance ?? 0) + amount;
    }, []);

    return (
      <Box ref={ref}>
        {/* Section title */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <ReceiptLong fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Recent Transactions
          </Typography>
        </Stack>

        {/* Top: Total + Page Jump */}
        <StandardTableToolbar
          page={page}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          showTotalCount
          showPageJump
        />

        {/* Table */}
        <Box mt={1}>
          <StandardTableFrame<PartnerTransactionRow>
            head={
              <TableRow>
                <TableCell width={70} align="right">
                  #
                </TableCell>
                <TableCell sx={{ minWidth: 120, pl: 2.5 }}>Transaction</TableCell>
                <TableCell>Transaction Code</TableCell>
                <TableCell>Order Code</TableCell>
                <TableCell>Product Code</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Prev Balance (USD)</TableCell>
                <TableCell align="right">Amount (USD)</TableCell>
                <TableCell align="right">Final Balance (USD)</TableCell>
                <TableCell align="center">Date (KST)</TableCell>
              </TableRow>
            }
            rows={rows}
            getRowKey={(t) => t.partnerTransactionId}
            columnsCount={11}
            isLoading={isLoading}
            loadingVariant="skeleton"
            skeletonRows={skeletonRows}
            emptyText="No transactions found."
            minWidth={1180}
            renderRow={(t, index) => {
              const amount = Number(t.addedAmount ?? 0);
              const finalBalance = getFinalBalance(t);

              return (
                <>
                  <TableCell align="right" sx={cellSx.number}>
                    {(page - 1) * pageSize + index + 1}
                  </TableCell>

                  <TableCell sx={{ ...cellSx.nowrap, minWidth: 120, pl: 2.5 }}>
                    <PartnerTransactionTypeDisplay type={t.type} />
                  </TableCell>

                  <TableCell sx={cellSx.mono}>{t.partnerTransactionCode}</TableCell>
                  <TableCell sx={cellSx.mono}>{t.orderCode}</TableCell>
                  <TableCell sx={cellSx.mono}>{t.productCode}</TableCell>

                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t.productName}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={cellSx.number}>
                    {t.quantity}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdNumberDisplay(t.previousBalance)}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdNumberDisplay(amount)}
                  </TableCell>

                  <TableCell align="right" sx={cellSx.money}>
                    {toUsdNumberDisplay(finalBalance)}
                  </TableCell>

                  <TableCell align="center" sx={cellSx.nowrap}>
                    {timestampToDate(t.createdAt)}
                  </TableCell>
                </>
              );
            }}
          />
        </Box>

        {/* Bottom: Pagination */}
        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount ?? 0}
            onChangePage={handleChangePage}
            size="medium"
            shape="rounded"
          />
        </Box>
      </Box>
    );
  },
);

RecentTransactionsTable.displayName = 'RecentTransactionsTable';

export default RecentTransactionsTable;
